import { generateId } from '@freelancer/datastore/testing/helpers';
import {
  ChallengeElementSourceTypeApi,
  ChallengeImageTypeApi,
  ChallengeStatusApi,
} from 'api-typings/challenges/challenges';
import { v4 } from 'uuid';
import type { Contest } from '../contests';
import type { Timeline } from '../timelines';
import type {
  Challenge,
  ChallengeContestElement,
  ChallengeElement,
  ChallengeImages,
  ChallengeUiProperties,
  ChallengeUnlinkedElement,
} from './challenges.model';

export interface GenerateChallengeOptions {
  readonly id?: number;
  readonly userId?: number;
  readonly title?: string;
  readonly subtitle?: string;
  readonly description?: string;
  readonly prize?: number;
  readonly enterpriseId?: number;
  readonly enterpriseInternalName?: string;
  readonly landingPageUrl?: string;
  readonly images?: ChallengeImages;
  readonly elements?: readonly ChallengeElement[];
  readonly jobIds?: readonly number[];
  readonly jobNames?: readonly string[];
  readonly partnerEnterpriseIds?: readonly number[];
  readonly partnerEnterpriseNames?: readonly string[];
  readonly currencyId?: number;
  readonly status?: ChallengeStatusApi;
  readonly rankOrder?: number;
  readonly timePublished?: number;
  readonly timeUpdated?: number;
  readonly timeDeleted?: number;
  readonly uiPropertiesOptions?: Partial<ChallengeUiProperties>;
}

export interface GenerateChallengesOptions {
  readonly challenges: readonly GenerateChallengeOptions[];
}

/**
 * Returns a challenge object. By default, this is a challenge that is "Active".
 */
export function generateChallengeObject({
  id = generateId(),
  userId = generateId(),
  title = 'A challenge title',
  subtitle = '',
  description = 'A challenge description',
  prize = 100_000,
  enterpriseId = generateId(),
  enterpriseInternalName = '',
  landingPageUrl,
  currencyId = 1,
  status = ChallengeStatusApi.ACTIVE,
  rankOrder = Infinity,
  images = generateChallengeImages(),
  elements = [],
  jobIds = [],
  jobNames = [],
  partnerEnterpriseIds = [],
  partnerEnterpriseNames = [],
  timePublished = Date.now(),
  timeUpdated = Date.now(),
  timeDeleted,
  uiPropertiesOptions = {},
}: GenerateChallengeOptions): Challenge {
  const uiProperties: ChallengeUiProperties = {
    bannerTitleColor: '#FFFFFF',
    bannerSubtitleColor: '#FFD747',
    cardTitleColor: '#FFFFFF',
    cardSubtitleColor: '#FFD747',
    ...uiPropertiesOptions,
  };

  return {
    id,
    userId,
    title,
    subtitle,
    description,
    prize,
    enterpriseId,
    enterpriseInternalName,
    landingPageUrl,
    currencyId,
    status,
    rankOrder,
    images,
    elements,
    jobIds,
    jobNames,
    partnerEnterpriseIds,
    partnerEnterpriseNames,
    timePublished,
    timeUpdated,
    timeDeleted,
    uiProperties,
  };
}

export function generateChallengeObjects({
  challenges,
}: GenerateChallengesOptions): readonly Challenge[] {
  return challenges.map(challenge => generateChallengeObject(challenge));
}

export function generateChallengeImages(): ChallengeImages {
  return {
    card: {
      height: 400,
      type: ChallengeImageTypeApi.CARD,
      url: 'test.png',
      fileId: v4(),
      width: 300,
    },
    bannerMobile: {
      height: 400,
      type: ChallengeImageTypeApi.BANNER_MOBILE,
      url: 'test.png',
      fileId: v4(),
      width: 300,
    },
    bannerDesktop: {
      height: 400,
      type: ChallengeImageTypeApi.BANNER_DESKTOP,
      url: 'test.png',
      fileId: v4(),
      width: 300,
    },
  };
}

// Mixins

export function withContestChallengeElement({
  contest,
  contestId,
  name = 'Contest element',
  rankOrder = 0,
  timelineDetails,
}: {
  readonly contest?: Contest;
  readonly contestId: number;
  readonly name?: string;
  readonly rankOrder?: number;
  readonly timelineDetails?: Timeline;
}): ChallengeContestElement {
  return {
    id: generateId(),
    contestDetails: contest,
    name,
    rankOrder,
    sourceId: contestId,
    sourceType: ChallengeElementSourceTypeApi.CONTEST,
    timelineDetails,
  };
}

export function withUnlinkedChallengeElement({
  name = 'Unlinked element',
  rankOrder = 0,
}: {
  readonly name?: string;
  readonly rankOrder?: number;
}): ChallengeUnlinkedElement {
  return {
    id: generateId(),
    rankOrder,
    name,
    sourceType: ChallengeElementSourceTypeApi.UNLINKED,
  };
}
